import * as React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { Home, Store, User } from "lucide-react";
// نسخة مصغَّرة (48×56) لمقاس العرض الحقيقيّ 24px — الأصل 82×96 كان أكبر من الحاجة
import navCartIcon from "@/assets/nav-cart-icon-sm.webp";
import { useCartStore } from "@/stores/cartStore";
import { useLocale } from "@/contexts/LocaleContext";
import { PwaInstallNudge } from "@/components/PwaInstallNudge";

/**
 * ناف بار سفليّ للموبايل فقط (md:hidden) — بستايل Nemro:
 * أيقونات خطّيّة رقيقة + تسمية sans UPPERCASE صغيرة.
 *
 * ارتفاعه يأتي من `--mobile-nav-height` في src/styles.css (مصدر حقيقة واحد).
 * الطبقة L3 (z-40) مثل الهيدر — فوق الـFAB (L2) وتحت الدراور (L4).
 */
export function MobileBottomNav() {
  const { pathname } = useLocation();
  const { t } = useLocale();
  const items = useCartStore((s) => s.items);
  const openCart = useCartStore((s) => s.openCart);
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  // 🚚 صفحات السائق: لا ناف بار متجر — لئلّا يخرج السائق من لوحته بالخطأ
  const isDriverRoute = pathname === "/driver" || pathname.startsWith("/driver/");

  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);

  const cellClass =
    "relative flex min-h-[44px] flex-col items-center justify-center gap-1 px-1 py-2 transition-colors";
  const labelClass = "t-micro whitespace-nowrap uppercase tracking-wider";

  const tone = (active: boolean) =>
    active ? "text-foreground font-medium" : "text-foreground/70";

  // مؤشّر الصفحة الحاليّة: شريط رقيق أعلى الخليّة
  const marker = (active: boolean) =>
    active ? (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-3 top-0 h-[2px] bg-foreground"
      />
    ) : null;

  if (isDriverRoute) return null;

  return (
    <nav
      aria-label={t("nav.home")}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-foreground/15 bg-background md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <PwaInstallNudge />
      <div className="grid grid-cols-4">
        <Link
          to="/"
          aria-current={isActive("/") ? "page" : undefined}
          className={`${cellClass} ${tone(isActive("/"))}`}
        >
          {marker(isActive("/"))}
          <Home className="h-5 w-5" strokeWidth={1.25} />
          <span className={labelClass}>{t("nav.home")}</span>
        </Link>

        <Link
          to="/shop"
          aria-current={isActive("/shop") ? "page" : undefined}
          className={`${cellClass} ${tone(isActive("/shop"))}`}
        >
          {marker(isActive("/shop"))}
          <Store className="h-5 w-5" strokeWidth={1.25} />
          <span className={labelClass}>{t("nav.shop")}</span>
        </Link>

        <button
          type="button"
          onClick={openCart}
          aria-label={t("cart.title")}
          className={`${cellClass} relative ${tone(false)}`}
        >
          <span className="relative">
            <img
              src={navCartIcon}
              alt=""
              aria-hidden="true"
              className="nav-cart-roll h-6 w-6 object-contain"
              loading="lazy"
              decoding="async"
            />
            {totalItems > 0 && (
              <span className="absolute -top-2 -end-3 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 t-micro font-medium text-primary-foreground">
                {totalItems}
              </span>
            )}
          </span>
          <span className={labelClass}>{t("nav.bottom_bag")}</span>
        </button>

        <Link
          to="/account"
          aria-current={isActive("/account") ? "page" : undefined}
          className={`${cellClass} ${tone(isActive("/account"))}`}
        >
          {marker(isActive("/account"))}
          <User className="h-5 w-5" strokeWidth={1.25} />
          <span className={labelClass}>{t("nav.bottom_account")}</span>
        </Link>
      </div>
    </nav>
  );
}
